import { NgClass } from '@angular/common';
import { Component, computed, Input, OnInit, signal, Signal, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BotMessageFeedback, ChatbotConfig, HistoryItemResponse, MessageContent } from '@shared/index';
import { MarkdownModule } from 'ngx-markdown';
import { AvatarImageService } from 'src/app/shared/services/avatar-image.service';
import { HighlightService } from '@shared-angular/services/highlight.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ICON_PAUSE } from '../../../../../../routes/ai-bot/avatar/SpeakWithPause';
import { LoginService } from '@shared-angular/services/login.service';
import { SocketService } from '../../../../../services/socket.service';
import { AudioBtnComponent } from '../audio-btn/audio-btn.component';

export type AiAnnotation = {
  type: "file_citation",
  text: string,
  start_index: number,
  end_index: number,
  file_citation?: {
    file_id: string
  }
}

export type Remark = BotMessageFeedback["remark"] | "";
@Component({
  selector: 'app-message-bubble',
  imports: [NgClass, AudioBtnComponent,
    MarkdownModule,
    FormsModule
  ],
  templateUrl: './message-bubble.component.html',
  styleUrl: './message-bubble.component.scss'
})
export class MessageBubbleComponent implements OnInit {
  @Input({ required: true }) public message!: HistoryItemResponse;
  public readonly bot = input.required<ChatbotConfig>();
  public readonly remark = input<Remark>('');
  public readonly annotations = input<AiAnnotation[]>([]);
  public readonly isMsgFromHistory = input<boolean>(false);
  public readonly botChatMsgSourceOption = input<'hidden' | 'visible' | 'linking'>('hidden');
  public readonly voiceOutputActive = input<boolean>(false);
  public readonly enableFeedback = input<boolean>(false);
  public readonly enableDeletion = input<boolean>(false);
  public readonly isThinking = input<boolean>(false);
  public readonly debugMode = input<boolean>(false);
  public readonly sendFeedback = output<{
    remark: Remark;
    msgId: string;
}>();
  public readonly deleteMessage = output<HistoryItemResponse>();

  public isHighlighted: Signal<boolean> = computed(() => { return this.message._id == this.highlightService.messageIdSource(); });
  public deleting: boolean = false;
  public buttons: string[] = [];
  public hasButtonsAtEnd: boolean = false;
  public messageContentWithLinks: string = '';

  public cleanMessageContent = signal("");

  constructor(
    public loginService: LoginService,
    private highlightService: HighlightService,
    private toastr: ToastrService,
    private socketService: SocketService,
    protected avatarimage: AvatarImageService
  ) {
    this.socketService.onRunUpdate.subscribe((data) => {
      if (data.item_id === this.message.item_id) {
        this.updateMessageContent();
      }
    });
  }

  public ngOnInit(): void {
    this.setMessageContent(this.message);
  }

  public highlightEntry() {
    if (!this.isHighlighted()) {
      this.highlightService.highlightMessage(this.message._id);
    } else {
      this.highlightService.highlightMessage("None");
    }
  }

  public setMessageContent(message: HistoryItemResponse) {
    const msg = this.getMessageContent(message.content);
    this.cleanMessageContent.set(msg.replaceAll(ICON_PAUSE, ''));
    this.buttons = this.extractButtonsFromMessage(msg);
    this.hasButtonsAtEnd = this.hasButtonsAtTheEnd(msg);
    this.messageContentWithLinks = this.extractMessageWithInnerLinks(msg);
    this.messageContentWithLinks = this.messageContentWithLinks.replaceAll(ICON_PAUSE, '');
  }

  public updateMessageContent() {
    const data = this.getMessageContent(this.message.content);
    this.cleanMessageContent.set(data.replaceAll(ICON_PAUSE, ''));
    this.buttons = this.extractButtonsFromMessage(data);
    this.hasButtonsAtEnd = this.hasButtonsAtTheEnd(data);
    this.messageContentWithLinks = this.extractMessageWithInnerLinks(data);
    this.messageContentWithLinks = this.messageContentWithLinks.replaceAll(ICON_PAUSE, '');
  }

  public extractMessageWithInnerLinks(message: string) {
    message = message.trim();
    let regex = this.getRegexForButtons();
    let buttons: string = "";
    while (regex.test(message)) {
      const match = regex.exec(message);
      if (match?.[1]) {
        const extractedText = "[[" + match[1] + "]]";
        buttons = extractedText + " " + buttons;
        message = message.slice(0, -extractedText.length).trim();
      }
    }
    if (this.isMsgFromHistory()) {
      let sourcing: string = "";
      regex = this.getRegexForSource();
      while (regex.test(message)) {
        const match = regex.exec(message);
        if (match) {
          const extractedText = "【" + match[1] + ":" + match[2] + "†source】";
          sourcing = extractedText + " " + sourcing;
          const botChatMsgSourceOption = this.botChatMsgSourceOption();
          if (botChatMsgSourceOption === 'hidden') {
            message = message.replace(extractedText, '');
          } else if (botChatMsgSourceOption === 'visible') {
            message = message.replace(extractedText, ' <i class="ri-chat-quote-line"></i>');
          } else {
            message = message.replace(extractedText, ' <i class="ri-chat-quote-fill"></i>');
          }
        }
      }
    }
    const matches = Array.from(this.getButtonMatches(message));
    for (const match of matches) {
      message = message.replace(match[0], '<span class="text-primary text-decoration-underline cursor-pointer message-button">' + match[1] + '</span>');
    }
    return message;
  }

  public extractButtonsFromMessage(message: string): string[] {
    message = message.trim();
    const regex = this.getRegexForButtons();
    const buttons: string[] = [];
    while (regex.test(message)) {
      const match = regex.exec(message);
      if (match?.[1]) {
        const extractedText = match[1];
        buttons.unshift(extractedText);
        message = message.slice(0, -(extractedText.length + 4)).trim();
      }
    }
    return buttons;
  }

  public hasButtonsAtTheEnd(message: string): boolean {
    const regexp = this.getRegexForButtons();
    return regexp.test(message.trim());
  }

  private getButtonMatches(message: string) {
    const regexp = /\[\[(.*?)]]/g;
    return message.matchAll(regexp);
  }

  public getRegexForButtons(): RegExp {
    return /[^[\]]*\[\[([^\]]*)]]$/;
  }

  public getRegexForSource(): RegExp {
    return /【([\0-9]+):([\0-9]+)†source】/;
  }

  public deleteMessageAndLoad(msg: HistoryItemResponse) {
    this.deleting = true;
    this.deleteMessage.emit(msg);
  }

  protected copyMsgToClipboard(msg: string) {
    navigator.clipboard.writeText(msg).then(() => {
      this.toastr.success("GENERAL", 'Nachricht kopiert');
    }).catch(() => {
      this.toastr.error("GENERAL", 'Nachricht konnte nicht kopiert werden');
    });
  }

  private getMessageContent(content: MessageContent[]): string {
    let m = '';
    const msg = content.at(0);
    if (msg?.type === 'text') {
      m = msg.text;
    }
    return m;
  }

  protected getUserMessageFormatted(content: MessageContent[]): string {
    const msg = this.getMessageContent(content);
    return msg.replaceAll('\n', '<br>');
  }

}
